import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

// Built-in Helvetica keeps the document metrically identical to Arial without
// parsing ~3.4MB of TTFs on the main thread every time the reader opens.
export const buyerSalePurchaseAgreementFileName =
  "(Buyer) Sale and Purchase Agreement Template.pdf";

const value = (v, fallback = "-") =>
  v === undefined || v === null || v === "" ? fallback : String(v);

export const buyerSalePurchaseAgreementSampleData = {
  date: "«DATE»",
  buyer: {
    name: "«BUYER_NAME»",
    identificationNo: "«ID_NO__REG_NO»",
    mailingAddress: [
      "«MAILING_ADDRESS_1»",
      "«MAILING_ADDRESS_2»",
      "«MAILING_ADDRESS_3»",
    ],
    mobileNo: "«MOBILE_NO»",
    email: "",
  },
  vehicle: {
    make: "«MAKE»",
    model: "«MODEL»",
    registrationNo: "«CAR_NO»",
    engineCapacity: "«ENGINE_CAPACITY»",
    engineNo: "«ENGINE_NO»",
    yearOfManufacture: "«YEAR_OF_MANUFACTURE»",
    chassisNo: "«CHASSIS_NO»",
    purchasePrice: "«AGREED_SELLING_PRICE»",
  },
  salesAgent: "«SALES_AGENT»",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 72,
    paddingRight: 72,
    paddingBottom: 54,
    paddingLeft: 72,
    fontFamily: "Helvetica",
    fontSize: 9,
    lineHeight: 1.16,
    color: "#000",
  },
  pageLegal: {
    paddingTop: 72,
    paddingRight: 72,
    paddingBottom: 54,
    paddingLeft: 72,
    fontFamily: "Helvetica",
    fontSize: 9,
    lineHeight: 1.08,
    color: "#000",
  },
  pageNo: {
    position: "absolute",
    bottom: 35,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
  },
  title: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textAlign: "center",
    paddingBottom: 3,
    borderBottomWidth: 0.55,
    borderBottomColor: "#000000",
  },
  topDate: {
    marginTop: 8,
    marginBottom: 9,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
  },
  sectionHeading: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 8,
  },
  sectionLetter: { width: 36, fontFamily: "Helvetica-Bold" },
  sectionHeadingText: { fontFamily: "Helvetica-Bold" },
  fieldRow: {
    display: "flex",
    flexDirection: "row",
    minHeight: 12,
    paddingLeft: 6,
  },
  fieldLabel: { width: 151, fontFamily: "Helvetica-Bold" },
  fieldColon: { width: 14, fontFamily: "Helvetica-Bold" },
  fieldValue: { flex: 1, fontFamily: "Helvetica-Bold" },
  fieldGap: { height: 7 },
  sectionGap: { height: 23 },
  paymentSectionGap: { height: 31 },
  firstSectionHeading: { marginBottom: 18 },
  vehicleConditionRow: {
    display: "flex",
    flexDirection: "row",
    marginTop: 2,
    marginBottom: 13,
    paddingLeft: 6,
  },
  vehicleConditionLabel: { width: 151, fontFamily: "Helvetica-Bold" },
  vehicleConditionColon: { width: 14, fontFamily: "Helvetica-Bold" },
  vehicleConditionValue: { flex: 1 },
  heading: {
    fontFamily: "Helvetica-Bold",
    marginTop: 12,
    marginBottom: 8,
  },
  listRow: { display: "flex", flexDirection: "row", marginBottom: 8 },
  listNo: { width: 36 },
  listBody: { flex: 1, textAlign: "justify" },
  subListRow: { display: "flex", flexDirection: "row", marginBottom: 4 },
  subListNo: { width: 18 },
  subListBody: { flex: 1, textAlign: "justify" },
  introConfirm: { marginTop: 13, marginBottom: 15, lineHeight: 1.22 },
  signatureColumns: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signatureCol: { width: "45.5%" },
  signatureTitle: { marginBottom: 79 },
  signatureTitleSigned: { marginBottom: 6 },
  signatureImage: {
    height: 73,
    marginBottom: 0,
    objectFit: "contain",
    objectPositionX: "0%",
    objectPositionY: "100%",
  },
  signatureLine: { borderTopWidth: 0.8, borderTopColor: "#000", paddingTop: 3 },
  signatureSpacer: { height: 31 },
  pageTwoSubList: { paddingLeft: 54, marginTop: 1 },
  pageTwoList: { marginTop: 8 },
  witnessTitle: { marginTop: 34 },
  witnessLine: { marginTop: 68 },
  intentionallyBlank: {
    marginTop: 50,
    textAlign: "center",
    fontFamily: "Helvetica-Oblique",
    fontSize: 9,
  },
  legalTitle: {
    width: 207.9,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    marginBottom: 7,
    textAlign: "center",
  },
  columns: { display: "flex", flexDirection: "row", gap: 35.4 },
  column: { width: 207.9 },
  clauseRow: { display: "flex", flexDirection: "row", marginBottom: 3.15 },
  clauseNo: { width: 27 },
  clauseBody: { flex: 1, textAlign: "justify" },
  clauseRowL2: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 3.15,
    paddingLeft: 29,
  },
  clauseNoL2: { width: 34 },
  clauseBodyL2: { flex: 1, textAlign: "justify" },
  clauseRowL3: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 2.8,
    paddingLeft: 63,
  },
  clauseNoL3: { width: 27 },
  clauseBodyL3: { flex: 1, textAlign: "justify" },
  sectionLegal: {
    display: "flex",
    flexDirection: "row",
    fontFamily: "Helvetica-Bold",
    marginTop: 4,
    marginBottom: 4,
  },
  sectionLegalNo: { width: 27 },
  sectionLegalTitle: { flex: 1 },
  bold: { fontFamily: "Helvetica-Bold" },
  italic: { fontFamily: "Helvetica-Oblique" },
  noticeBox: {
    width: 145,
    marginTop: 7,
    marginBottom: 9,
    marginLeft: 63,
  },
  noticeRow: { display: "flex", flexDirection: "row" },
  noticeLabel: {
    width: 57.5,
    padding: 4,
    fontFamily: "Helvetica-Bold",
    backgroundColor: "#C1E4F5",
    borderTopWidth: 0.5,
    borderRightWidth: 0.5,
    borderLeftWidth: 0.5,
    borderColor: "#000000",
  },
  noticeValue: {
    flex: 1,
    padding: 4,
    borderTopWidth: 0.5,
    borderRightWidth: 0.5,
    borderColor: "#000000",
  },
  noticeLastCell: { borderBottomWidth: 0.5 },
  noticeIntro: { marginTop: 3, marginBottom: 3, marginLeft: 63 },
  endBlank: {
    marginTop: 18,
    textAlign: "center",
    fontFamily: "Helvetica-Oblique",
  },
});

function PageNumber({ n }) {
  return (
    <Text fixed style={styles.pageNo}>
      {n}
    </Text>
  );
}

function SectionHeading({ letter, children, spacious = false }) {
  return (
    <View
      style={[
        styles.sectionHeading,
        spacious && styles.firstSectionHeading,
      ]}
    >
      <Text style={styles.sectionLetter}>({letter})</Text>
      <Text style={styles.sectionHeadingText}>{children}</Text>
    </View>
  );
}

function FieldRow({ label, children }) {
  return (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={styles.fieldColon}>:</Text>
      <Text style={styles.fieldValue}>{children}</Text>
    </View>
  );
}

function ListRow({ n, children }) {
  return (
    <View style={styles.listRow}>
      <Text style={styles.listNo}>{n}.</Text>
      <Text style={styles.listBody}>{children}</Text>
    </View>
  );
}

function SubListRow({ n, children }) {
  return (
    <View style={styles.subListRow}>
      <Text style={styles.subListNo}>{n}.</Text>
      <Text style={styles.subListBody}>{children}</Text>
    </View>
  );
}

function Clause({ n, children, level = 1 }) {
  const rowStyle =
    level === 3
      ? styles.clauseRowL3
      : level === 2
        ? styles.clauseRowL2
        : styles.clauseRow;
  const noStyle =
    level === 3
      ? styles.clauseNoL3
      : level === 2
        ? styles.clauseNoL2
        : styles.clauseNo;
  const bodyStyle =
    level === 3
      ? styles.clauseBodyL3
      : level === 2
        ? styles.clauseBodyL2
        : styles.clauseBody;
  return (
    <View style={rowStyle} wrap={false}>
      <Text style={noStyle}>{n}</Text>
      <Text style={bodyStyle}>{children}</Text>
    </View>
  );
}

function LegalSectionHeading({ number, children }) {
  return (
    <View style={styles.sectionLegal} wrap={false}>
      <Text style={styles.sectionLegalNo}>{number}.</Text>
      <Text style={styles.sectionLegalTitle}>{children}</Text>
    </View>
  );
}

function NoticeRow({ label, children, last = false }) {
  return (
    <View style={styles.noticeRow} wrap={false}>
      <Text
        style={[styles.noticeLabel, last && styles.noticeLastCell]}
      >
        {label}
      </Text>
      <Text
        style={[styles.noticeValue, last && styles.noticeLastCell]}
      >
        {children}
      </Text>
    </View>
  );
}

export default function BuyerSalePurchaseAgreementPdf({
  data = buyerSalePurchaseAgreementSampleData,
}) {
  const buyer = data.buyer || {};
  const vehicle = data.vehicle || {};
  const address = Array.isArray(buyer.mailingAddress)
    ? buyer.mailingAddress
    : [buyer.mailingAddress].filter(Boolean);

  return (
    <Document
      title="SALE AND PURCHASE AGREEMENT"
      author="DCH Automobile Sdn. Bhd."
    >
      {/* PAGE 1 */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          SALE AND PURCHASE AGREEMENT (“Agreement”)
        </Text>
        <Text style={styles.topDate}>Date: {value(data.date)}</Text>

        <SectionHeading letter="A" spacious>
          Buyer’s details
        </SectionHeading>
        <FieldRow label="Name">{value(buyer.name)}</FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label={"Identification Number or\nRegistration Number"}>
          {value(buyer.identificationNo)}
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Mailing Address">
          {address.map((line) => value(line, "")).join("\n")}
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Mobile No.">{value(buyer.mobileNo)}</FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Email">{value(buyer.email, "")}</FieldRow>

        <View style={styles.sectionGap} />
        <SectionHeading letter="B">Vehicle’s description</SectionHeading>
        <FieldRow label="Make & Model">
          {value(vehicle.make, "")} {value(vehicle.model, "")}
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Vehicle Registration No.">
          {value(vehicle.registrationNo)}
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Engine Capacity">
          {value(vehicle.engineCapacity)}
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Engine No.">{value(vehicle.engineNo)}</FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Year of Manufacture">
          {value(vehicle.yearOfManufacture)}
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Chassis No.">{value(vehicle.chassisNo)}</FieldRow>
        <View style={styles.fieldGap} />
        <View style={styles.vehicleConditionRow}>
          <Text style={styles.vehicleConditionLabel}>
            Vehicle State and Condition
          </Text>
          <Text style={styles.vehicleConditionColon}>:</Text>
          <Text style={styles.vehicleConditionValue}>
            per the vehicle inspection report, a copy of which is annexed to
            this Agreement as Annexure 1 (“
            <Text style={styles.bold}>Report</Text>”).{"\n\n"}
            per the responses of the Owner per the questionnaire, a copy of
            which is annexed to this Agreement as Annexure 2 (“
            <Text style={styles.bold}>Questionnaire</Text>”).
          </Text>
        </View>
        <FieldRow label="Purchase Price">
          {value(vehicle.purchasePrice)}
        </FieldRow>

        <View style={styles.paymentSectionGap} />
        <SectionHeading letter="C">Payment</SectionHeading>
        <FieldRow label="Bank Account Name">DCH AUTOMOBILE SDN BHD</FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Bank Name & Address">
          STANDARD CHARTERED BANK MALAYSIA
        </FieldRow>
        <View style={styles.fieldGap} />
        <FieldRow label="Bank Account No.">708409939989</FieldRow>

        <Text style={styles.heading}>GENERAL TERMS</Text>
        <ListRow n="1">
          The buyer described in Section A above (“
          <Text style={styles.bold}>Buyer</Text>”) is desirous of purchasing the
          second-hand vehicle particularly described in Section B above (“
          <Text style={styles.bold}>Vehicle</Text>”) from DCH Automobile Sdn.
          Bhd. (“<Text style={styles.bold}>DCH</Text>”).
        </ListRow>
        <ListRow n="2">
          Upon the execution of this Agreement, the Buyer shall pay a deposit in
          the sum of Ringgit Malaysia Five Thousand (RM5,000.00) (“
          <Text style={styles.bold}>Deposit</Text>”) to DCH. The Parties agree
          that the Deposit shall form part of the purchase price of the Vehicle.
          Upon the payment of the Deposit, DCH shall remove the advertisement of
          the Vehicle from its platform.
        </ListRow>
        <ListRow n="3">
          In the event the Buyer is a cash buyer, the Buyer shall pay and settle
          the remaining balance of the purchase price of the Vehicle in the sum
          equivalent to the Purchase Price less the Deposit (“
          <Text style={styles.bold}>Balance Purchase Price</Text>”) no later than
          three (3) days from the date of this Agreement.
        </ListRow>
        <ListRow n="4">
          In the event the Buyer is obtaining financing from a financier (“
          <Text style={styles.bold}>Buyer Financier</Text>”) to finance the
          purchase of the Vehicle (“
          <Text style={styles.bold}>Vehicle Loan</Text>”), the Balance Purchase
          Price shall be paid in full and cleared funds in the following manner:
        </ListRow>
        <PageNumber n="1" />
      </Page>

      {/* PAGE 2 */}
      <Page size="A4" style={styles.page}>
        <View style={styles.pageTwoSubList}>
          <SubListRow n="a">
            the road tax, insurance premium, processing fee, car-plate change
            fees and all other fees prescribed by DCH shall be paid within three
            (3) days from the date DCH notifies the Buyer to make such payment;
          </SubListRow>
          <SubListRow n="b">
            the sum equivalent to the difference between the Balance Purchase
            Price and the Vehicle Loan amount (“
            <Text style={styles.bold}>Balance Downpayment</Text>”) shall be paid
            in full to DCH within 3 days from the date of the Buyer Financier’s
            letter of undertaking, setting out the Buyer Financier’s undertaking
            to release the Vehicle Loan (“
            <Text style={styles.bold}>LOU</Text>”); and
          </SubListRow>
          <SubListRow n="c">
            the sum equivalent to the difference between the Balance Purchase
            Price and the Balance Downpayment shall be released and paid to DCH
            within 21 days from the date of the LOU.
          </SubListRow>
        </View>
        <View style={styles.pageTwoList}>
          <ListRow n="5">
            The Deposit, the Balance Purchase Price and all other fees and
            expenses mentioned above shall be payable by the to DCH through the
            payment method as stated in Section C above or in such other or
            account prescribed by DCH.
          </ListRow>
          <ListRow n="6">
            DCH and the Buyer further agree to be bound by the terms and
            conditions included in the following page.
          </ListRow>
        </View>

        <Text style={styles.introConfirm}>
          I, the undersigned, confirm that I have read the provisions printed on
          the front and back sides of this agreement and I accept all the terms
          and provisions of this Agreement.
        </Text>

        <View style={styles.signatureColumns}>
          <View style={styles.signatureCol}>
            <Text style={styles.signatureTitle}>DCH Automobile Sdn. Bhd.</Text>
            <View style={styles.signatureLine}>
              <Text>Name: VIVIAN VOON</Text>
              <Text>Designation: ADMIN</Text>
            </View>
            <Text style={styles.witnessTitle}>Witness</Text>
            <View style={[styles.signatureLine, styles.witnessLine]}>
              <Text>Name: KYLE WAN KHAI YANG</Text>
              <Text>NRIC No.: 910706-08-6217</Text>
            </View>
          </View>
          <View style={styles.signatureCol}>
            {data.buyerSignature ? (
              <>
                <Text style={styles.signatureTitleSigned}>Buyer</Text>
                <Image src={data.buyerSignature} style={styles.signatureImage} />
              </>
            ) : (
              <Text style={styles.signatureTitle}>Buyer</Text>
            )}
            <View style={styles.signatureLine}>
              <Text>Name: {value(buyer.name)}</Text>
              <Text>NRIC No.: {value(buyer.identificationNo)}</Text>
            </View>
            <Text style={styles.witnessTitle}>Witness</Text>
            <View style={[styles.signatureLine, styles.witnessLine]}>
              <Text>Name: {value(data.salesAgent)}</Text>
              <Text>NRIC No.:</Text>
            </View>
          </View>
        </View>
        <Text style={styles.intentionallyBlank}>
          [The remaining of this page is intentionally left blank]
        </Text>
        <PageNumber n="2" />
      </Page>

      {/* PAGE 3 */}
      <Page size="A4" style={styles.pageLegal}>
        <Text style={styles.legalTitle}>TERMS AND CONDITIONS</Text>
        <View style={styles.columns}>
          <View style={styles.column}>
            <LegalSectionHeading number="1">
              SALE AND PURCHASE OF VEHICLE
            </LegalSectionHeading>
            <Clause n="1.1">The Parties agree and acknowledge that:</Clause>
            <Clause n="1.1.1" level={2}>
              DCH hereby sells and the Buyer hereby purchases the Vehicle from
              DCH at the purchase price as set out in Section B above and that
              the purchase price is agreed upon by the Parties on a willing
              buyer willing seller basis (“
              <Text style={styles.bold}>Purchase Price</Text>”);
            </Clause>
            <Clause n="1.1.2" level={2}>
              the Buyer has inspected the state and condition of the Vehicle to
              his satisfaction (“
              <Text style={styles.bold}>Vehicle State & Condition</Text>”);
            </Clause>
            <Clause n="1.1.3" level={2}>
              the Vehicle is sold to the Buyer on “as-is” basis without any
              warranties, except for the warranties expressly stated in this
              Agreement;
            </Clause>
            <Clause n="1.1.4" level={2}>
              DCH shall not be responsible for any repair, maintenance and/or
              modification of the Vehicle upon the execution of this Agreement;
            </Clause>
            <Clause n="1.1.5" level={2}>
              the Buyer shall pay the Deposit (as hereinafter defined), the
              Balance Purchase Price (as hereinafter defined) and all other fees
              and expenses to DCH within the timeframe stipulated in this
              Agreement;
            </Clause>
            <Clause n="1.1.6" level={2}>
              in the event the Buyer is a cash buyer, upon the complete payment
              of the Balance Purchase Price (as hereinafter defined) and any
              other fees payable to DCH in accordance with the terms and
              conditions of this Agreement, DCH shall, on a date mutually agreed
              upon by the Parties, ensure that:
            </Clause>
            <Clause n="(a)" level={3}>
              DCH delivers and the Buyer receives and accepts the possession of
              the Vehicle; and
            </Clause>
            <Clause n="(b)" level={3}>
              the Parties shall proceed with Completion in the manner as set out
              in Clause 2;
            </Clause>
            <Clause n="1.1.7" level={2}>
              Completion (as hereinafter defined) shall take place in accordance
              with such timeline as set out in this Agreement (differing
              depending on whether the Purchaser is a cash buyer or obtaining
              the Vehicle Loan from the Buyer Financier) or such extended period
              as DCH may determine. Accordingly, any necessary work, processes
              and payments required to facilitate the Completion shall be
              completed within the aforesaid timeframe. The Buyer shall ensure
              that all obligations, including but not limited to documentation
              and payments are fulfilled within the timeframe set out in this
              Agreement;
            </Clause>
            <Clause n="1.1.8" level={2}>
              If the Buyer is obtaining the Vehicle Loan from the Buyer
              Financier to finance the purchase of the Vehicle, the Buyer
              agrees, covenants and undertakes as follows:
            </Clause>
          </View>
          <View style={styles.column}>
            <Clause n="(a)" level={3}>
              to provide all necessary information and documents as may be
              requested by DCH and/or the Buyer Financier, in order to
              facilitate and process the Vehicle Loan approval process and
              issuance of the LOU;
            </Clause>
            <Clause n="(b)" level={3}>
              to procure the Buyer Financier to issue the LOU within 7 days from
              the date of this Agreement, whereupon the LOU shall be on such
              terms acceptable to DCH;
            </Clause>
            <Clause n="(c)" level={3}>
              to fulfil all the terms of the LOU within 5 days from the date of
              the LOU, all at the costs and expense of the Buyer;
            </Clause>
            <Clause n="(d)" level={3}>
              the Buyer shall adhere and comply with all instructions prescribed
              by DCH, including but not limited to completion of the biometric
              authentication at the relevant Road Transport Department (“
              <Text style={styles.bold}>JPJ</Text>”) office or at any location or
              platform prescribed by DCH, at such timeline prescribed by DCH;
              and
            </Clause>
            <Clause n="(e)" level={3}>
              the Buyer shall do all things necessary to procure the Buyer
              Financier to release the Vehicle Loan to DCH, in any event no
              later than 21 days from the date of the LOU.
            </Clause>
            <Clause n="1.1.9" level={2}>
              in the event the Buyer fails, neglects and/or refuses to
              facilitate the Completion with any of its obligations as set forth
              under this Agreement at such timeline set forth in this Agreement
              or as prescribed by DCH, DCH shall be entitled, without further
              notice to the Buyer, to advertise, offer and/or sell the Vehicle
              to any other party at its sole discretion and shall further be
              entitled to terminate this Agreement, effective immediately;
            </Clause>
            <Clause n="1.1.10" level={2}>
              in the event the Buyer fails to settle all necessary payments for
              the purchase of the Vehicle including the Deposit, the Balance
              Purchase Price and any other fees payable to DCH within the
              timeframe stipulated in this Agreement, DCH shall be entitled to
              charge the Buyer an interest at the rate of 12% per annum
              calculated on a daily basis, which will be imposed on the first
              day the amount is due until the date of the full settlement; and
            </Clause>
            <Clause n="1.1.11" level={2}>
              DCH shall be entitled to forfeit the Deposit (as hereinafter
              defined) if the Buyer cancels and/or withdraws the purchase of the
              Vehicle at any time before the Completion (as
            </Clause>
          </View>
        </View>
        <PageNumber n="3" />
      </Page>

      {/* PAGE 4 */}
      <Page size="A4" style={styles.pageLegal}>
        <View style={styles.columns}>
          <View style={styles.column}>
            <Clause n="" level={2}>
              hereinafter defined). For the avoidance of doubt, upon the
              execution of this Agreement, the Buyer shall be bound to complete
              the purchase of the Vehicle in accordance with the terms of this
              Agreement.
            </Clause>
            <LegalSectionHeading number="2">
              TRANSFER OF OWNERSHIP
            </LegalSectionHeading>
            <Clause n="2.1">
              Upon receipt of payment of the Balance Purchase Price, DCH shall
              do all that is necessary, within a reasonable timeframe, to
              facilitate and complete the transfer of ownership of the Vehicle
              to the Buyer in accordance with the applicable laws, including but
              not limited to the requirements fixed by the Road Transport
              Department and any other relevant authorities.
            </Clause>
            <Clause n="2.2">
              The Buyer agrees to give full cooperation to DCH and shall
              promptly provide all necessary documents and/or information as may
              be reasonably requested by DCH in order to effect such transfer.
            </Clause>
            <Clause n="2.3">
              In the event the Buyer fails to provide the requested documents
              and/or information within reasonable timeframe, DCH shall not be
              held liable for any delay and/or penalties resulting from such
              failure, and the Buyer shall bear responsibility for any
              additional costs that may be incurred as a result.
            </Clause>
            <Clause n="2.4">
              The transfer of ownership of the Vehicle shall be deemed complete
              upon confirmation from the Road Transport Department that the
              Vehicle is registered in the Buyer’s name (“
              <Text style={styles.bold}>Completion</Text>”). Upon the Completion,
              DCH shall deliver all necessary documents to the Buyer evidencing
              the change of ownership of the Vehicle to the Buyer.
            </Clause>
            <LegalSectionHeading number="3">
              DELIVERY OF THE VEHICLE
            </LegalSectionHeading>
            <Clause n="3.1">
              Upon the Completion, DCH shall arrange for the delivery of the
              Vehicle to the Buyer at a location and time as mutually agreed
              between the Parties, or alternatively, DCH shall make the Vehicle
              available for collection by the Buyer at a designated location
              notified by DCH.
            </Clause>
            <Clause n="3.2">
              Any costs associated with the delivery of the Vehicle, including
              transportation fees or related expenses, shall be borne by the
              Buyer, unless otherwise agreed in writing. DCH shall notify the
              Buyer of any such costs and shall provide reasonable assistance in
              facilitating the deliver or collection of the Vehicle.
            </Clause>
            <Clause n="3.3">
              The Vehicle shall be delivered or made available for collection in
              accordance with the Vehicle State & Condition. The Buyer shall
              confirm receipt of the Vehicle by signing an acknowledgement of
              delivery at the time of delivery or collection.
            </Clause>
            <Clause n="3.4">
              Upon the delivery or collection of the Vehicle, DCH shall not be
              liable for any damage, loss or liability to the Vehicle and such
              damage, loss or liability shall entirely be the responsibility of
              the Buyer.
            </Clause>
          </View>
          <View style={styles.column}>
            <LegalSectionHeading number="4">
              REPRESENTATION AND WARRANTIES
            </LegalSectionHeading>
            <Clause n="4.1">
              DCH hereby represents, undertakes and warrants that: -
            </Clause>
            <Clause n="4.1.1" level={2}>
              it will ensure that the Vehicle is delivered to the Buyer in the
              same Vehicle State & Condition;
            </Clause>
            <Clause n="4.1.2" level={2}>
              the Vehicle is free and will be free from all liens, encumbrances,
              charges and security interests;
            </Clause>
            <Clause n="4.1.3" level={2}>
              the Vehicle has undergone internal visual inspection and the
              necessary approvals following such inspection have been obtained
              from PUSPAKOM and Road Transport Department Malaysia; and
            </Clause>
            <Clause n="4.1.4" level={2}>
              it has the power and capacity to execute, deliver and perform the
              terms of this Agreement without violating any law or regulation.
            </Clause>
            <Clause n="4.2">
              DCH warrants that all the above representations and warranties
              will be fulfilled down to and will be true and correct throughout
              the Term in all aspects. DCHDCH undertakes to immediately inform
              the Buyer by a written notice in the event of breach of any of the
              above representations or warranties.
            </Clause>
            <LegalSectionHeading number="5">TERMINATION</LegalSectionHeading>
            <Clause n="5.1">
              DCH shall be entitled to terminate this Agreement: -
            </Clause>
            <Clause n="5.1.1" level={2}>
              at any time prior to Completion, by providing twenty-four (24)
              hours written notice to the Buyer, without affording any reason to
              the Buyer and the Parties agree that any such termination
              according to clause 5.1.1 shall not result in any financial
              penalty or liability to DCH;
            </Clause>
            <Clause n="5.1.2" level={2}>
              immediately, if the Buyer commits a breach of this Agreement and
              fails to remedy such breaches in the period prescribed by DCH;
            </Clause>
            <Clause n="5.1.3" level={2}>
              immediately, if the Buyer fails to comply with applicable laws and
              regulations related to the purchase and transfer of the Vehicle.
            </Clause>
            <Clause n="5.2">
              In the event this Agreement is terminated according to clauses
              5.1.2 or 5.1.3, the Parties agree that DCH shall be entitled to
              claim liquidated ascertained damages in the sum of Ringgit
              Malaysia Five Thousand (RM5,000.00), in addition to forfeiting the
              Deposit..
            </Clause>
            <Clause n="5.3">
              The Buyer shall be entitled to terminate this Agreement: -
            </Clause>
            <Clause n="5.3.1" level={2}>
              if any of the representations and warranties provided by DCH are
              found to be untrue, false or inaccurate; or
            </Clause>
            <Clause n="5.3.2" level={2}>
              if DCH commits a material breach, or a series of breaches
              resulting in a material breach, of the Agreement and fails to
              remedy such breaches in the period prescribed by the Buyer.
            </Clause>
            <Clause n="5.4">
              In the event this Agreement is terminated according to clause, DCH
              shall refund the
            </Clause>
          </View>
        </View>
        <PageNumber n="4" />
      </Page>

      {/* PAGE 5 */}
      <Page size="A4" style={styles.pageLegal}>
        <View style={styles.columns}>
          <View style={styles.column}>
            <Clause n="" level={1}>
              Buyer any payment made under this Agreement, excluding any accrued
              interest.
            </Clause>
            <Clause n="5.5">
              Upon the termination of this Agreement under clauses 5.1 or 5.3
              and in the event the termination occurs after the Vehicle has been
              delivered to the Buyer, the Buyer shall immediately:
            </Clause>
            <Clause n="5.5.1" level={2}>
              return the Vehicle back to DCH;
            </Clause>
            <Clause n="5.5.2" level={2}>
              effect the transfer of ownership of the Vehicle to DCH (if
              applicable); and
            </Clause>
            <Clause n="5.5.3" level={2}>
              return all documents in relation to the Vehicle back to DCH.
            </Clause>
            <Clause n="5.6">
              Any termination shall be without prejudice to the accrued rights
              and liabilities of the Parties and any obligations which survive
              termination under this Agreement.
            </Clause>
            <LegalSectionHeading number="6">INDEMNITY</LegalSectionHeading>
            <Clause n="6.1">
              The Buyer agrees to indemnify and hold harmless DCH, its
              directors, employees and/or agents from and against any and all
              claims, demands, actions, suits, proceedings, liabilities,
              damages, losses, costs, and expenses arising out of or in
              connection with any act, default, delay, omissions or
              non-performance on the part of the Buyer in relation to the
              Vehicle under this Agreement.
            </Clause>
            <Clause n="6.2">
              Clause 6.1 shall be binding on the Buyer’s successors in title,
              representatives and assigns and is expressly intended to be and
              shall be a continuing indemnity whatsoever now or hereafter and
              shall remain effective until DCH’s claim(s) under this Clause 6
              have been fully satisfied.
            </Clause>
            <LegalSectionHeading number="7">MISCELLANEOUS</LegalSectionHeading>
            <Clause n="7.1">
              <Text style={styles.bold}>Limitation of Liability: </Text>
              To the maximum extent permitted by law, DCH’s total liability to
              the Buyer for any and all claims, losses, costs, or damages
              arising out of or related to this Agreement, whether in contract,
              tort (including negligence), or otherwise, shall be limited to one
              per centum (1%) of the total purchase price received by DCH. In no
              event shall DCH be liable to the Buyer for any indirect,
              incidental, consequential, special, or punitive damages, including
              but not limited to loss of profits, loss of business
              opportunities, loss of goodwill, or business interruption, arising
              out of or in connection with this Agreement, even if DCH has been
              advised of the possibility of such damages.
            </Clause>
            <Clause n="7.2">
              <Text style={styles.bold}>Time: </Text>Time whenever mentioned is
              the essence of this Agreement, both as regards the date and period
              specifically mentioned and as to any dates and periods which may
              be agreed in writing between the Parties.
            </Clause>
            <Clause n="7.3">
              <Text style={styles.bold}>Amendment: </Text>Any waiver,
              modification or amendment made by either Party to this Agreement
              shall not be effective unless made in writing and duly signed by
              each of the Party hereto.
            </Clause>
            <Clause n="7.4">
              <Text style={styles.bold}>Severability: </Text>The
              unenforceability of any part of this Agreement shall not affect
              the
            </Clause>
          </View>
          <View style={styles.column}>
            <Clause n="" level={1}>
              enforceability of any other part.
            </Clause>
            <Clause n="7.5">
              <Text style={styles.bold}>Notice:</Text>
            </Clause>
            <Clause n="7.5.1" level={2}>
              All notices, requests or demands (each a{" "}
              <Text style={styles.bold}>notice</Text>) required to be given, made
              or served for any purpose under this Agreement shall either be
              delivered personally to the person to whom it is addressed, or
              left at or sent by A.R. registered post to the person’s address or
              emailed in accordance with the addressee’s email or sent by
              WhatsApp or other mobile messaging service prescribed by DCH. The
              details of DCH are set out below while the details of the Buyer are
              set out in this Agreement:
            </Clause>
            <Text style={styles.noticeIntro}>
              DCH Automobile SDN. BHD. (Registration No. 202301046619
              (1540533-U))
            </Text>
            <View style={styles.noticeBox}>
              <NoticeRow label="Registered Address">
                No. 7-4, Jalan USJ 9/5Q, Subang Business Centre, UEP Subang
                Jaya, 47620 Subang Jaya, Selangor
              </NoticeRow>
              <NoticeRow label="Business Address">
                No. 7, Jalan Astaka 1F/KU2, Bandar Bukit Raja, 41050 Klang,
                Selangor
              </NoticeRow>
              <NoticeRow label="Email">
                admin_dchautomobile@dhub.my
              </NoticeRow>
              <NoticeRow label="Attention to" last>
                Mr. Kyle Wan / Ms. Vivian Voon
              </NoticeRow>
            </View>
            <Clause n="7.5.2" level={2}>
              A notice is taken as given by a Party (“
              <Text style={styles.bold}>Sender</Text>”) and received by the other
              Party (“<Text style={styles.bold}>Recipient</Text>”) if:
            </Clause>
            <Clause n="(a)" level={3}>
              by hand delivery, upon delivery;
            </Clause>
            <Clause n="(b)" level={3}>
              by A.R. registered post, five (5) Business Days from the date of
              postage;
            </Clause>
            <Clause n="(c)" level={3}>
              by courier service, at the time of the Recipient’s receipt;
            </Clause>
            <Clause n="(d)" level={3}>
              by electronic mail, at the time of transmission as shown on the
              date stamp of the electronic mail; or
            </Clause>
            <Clause n="(e)" level={3}>
              if sent by WhatsApp or other mobile messaging service, at the time
              the message is delivered and read, as evidenced by a delivery and
              read receipt from the application.
            </Clause>
            <Clause n="7.5.3" level={2}>
              But if delivery or receipt is on a day which is not a Business Day
              or is after 5.00 pm at the place of delivery or receipt, it is
              deemed received at 9.00 am on the next Business Day. For the
              avoidance of doubt, ‘
              <Text style={styles.bold}>Business Day</Text>’ means a day when
            </Clause>
          </View>
        </View>
        <PageNumber n="5" />
      </Page>

      {/* PAGE 6 */}
      <Page size="A4" style={styles.pageLegal}>
        <View style={styles.columns}>
          <View style={styles.column}>
            <Clause n="" level={2}>
              banks are opened for business in the State of Selangor, other than
              a Saturday, Sunday or gazette public holiday in the State of
              Selangor.
            </Clause>
            <Clause n="7.6">
              <Text style={styles.bold}>Waiver:</Text>
            </Clause>
            <Clause n="7.6.1" level={2}>
              A Party waives a right under this Agreement only if it does so in
              writing.
            </Clause>
            <Clause n="7.6.2" level={2}>
              A Party does not waive a right simply because it:
            </Clause>
            <Clause n="(a)" level={3}>
              fails to exercise the right;
            </Clause>
            <Clause n="(b)" level={3}>
              delays in exercising the right; or
            </Clause>
            <Clause n="(c)" level={3}>
              only exercise part of the right.
            </Clause>
            <Clause n="7.6.3" level={2}>
              A waiver of one breach of a term of this Agreement does not
              operate as a waiver of another breach of the same term or any
              other term.
            </Clause>
            <Clause n="7.7">
              <Text style={styles.bold}>Successors and Assigns: </Text>This
              Agreement shall be binding upon and shall inure to the benefit of
              the Parties and their respective successors and permitted assigns.
            </Clause>
            <Clause n="7.8">
              <Text style={styles.bold}>Entire Agreement: </Text>This Agreement
              replaces any previous agreement, representation, warranty or
              understanding between the Parties concerning the subject matter
              and contains the entire agreement between the Parties. This
              Agreement embodies the entire agreement and understanding of the
              Parties and there are no provisions, terms, conditions or
              obligations, oral or written, express or implied other than those
              contained herein and in the event of this Agreement being found
              inconsistent with any other document or documents signed by them
              prior to the execution of this Agreement, the terms of this
              Agreement shall prevail. For the avoidance of doubt, all earlier
              rights of the Parties prior to this Agreement are extinguished.
            </Clause>
            <Clause n="7.9">
              <Text style={styles.bold}>Counterparts: </Text>This Agreement may
              be executed in any number of counterparts and by different Parties
              on separate counterparts each of which when executed and delivered
              shall constitute an original, but all the counterparts shall
              together constitute one and the same instrument.
            </Clause>
            <Clause n="7.10">
              <Text style={styles.bold}>Governing Law and Jurisdiction: </Text>
              This Agreement shall be governed by and construed in accordance
              with the laws of Malaysia. Any dispute, controversy or claim
              arising out of or in connection with this Agreement, or the
              breach, termination or invalidity thereof, shall be finally
              submitted and settled by the Courts of Malaysia.
            </Clause>
            <Clause n="7.11">
              <Text style={styles.bold}>Cost: </Text>Each Party shall pay its
              own costs and disbursements of and incidental to the negotiation,
              preparation and completion of this Agreement. Notwithstanding
              anything to the contrary, all duty chargeable and payable pursuant
              to the Stamp Act 1949 shall be borne by the Buyer.
            </Clause>
            <Clause n="7.12">
              <Text style={styles.bold}>Further Assurance: </Text>The Parties
              shall do all further acts and execute all further documents
              necessary to give effect to this Agreement.
            </Clause>
            <Clause n="7.13">
              <Text style={styles.bold}>References to Clauses: </Text>References
              to clauses, sections, schedules, and appendices, unless otherwise
              specified, are to
            </Clause>
          </View>
          <View style={styles.column}>
            <Clause n="" level={1}>
              clauses, sections, schedules, and appendices of this Agreement,
              and the same shall form part and parcel of this Agreement.
            </Clause>
            <Clause n="7.14">
              <Text style={styles.bold}>Headings: </Text>All headings,
              sub-headings, and the table of contents herein are for reference
              purposes only and do not form part of this Agreement and shall not
              be deemed to alter or affect the meaning of any provisions hereof.
            </Clause>
            <Clause n="7.15">
              <Text style={styles.bold}>Persons and Assigns: </Text>Words
              denoting persons shall include their respective heirs, personal
              representatives, successors-in-title, or permitted assigns, as the
              case may be.
            </Clause>
            <Clause n="7.16">
              <Text style={styles.bold}>Construction Rules: </Text>No rule of
              construction shall apply to the detriment of either Party by
              reason of that Party having control and/or being responsible for
              the preparation of this Agreement or any part thereof.
            </Clause>
            <Clause n="7.17">
              <Text style={styles.bold}>References to Agreements: </Text>
              References to “this Agreement” or any other agreement or document
              shall be construed as references to such agreement or document as
              amended, modified, or supplemented and in effect from time to time
              and shall include references to any document which amends,
              modifies, or supplements it, or is entered into, made, or given
              pursuant to or in accordance with its terms.
            </Clause>
            <Text style={styles.endBlank}>
              [THE REST OF THIS PAGE IS INTENTIONALLY LEFT BLANK]
            </Text>
          </View>
        </View>
        <PageNumber n="6" />
      </Page>
    </Document>
  );
}
